import React, { useEffect, useState } from 'react';
import { api, brl } from '../lib/api';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from 'recharts';
import { TrendingUp, DollarSign, Package, ShoppingBag, AlertTriangle, Boxes, Wallet, Activity } from 'lucide-react';

const PERIODS = [
  { id: 'today', label: 'Hoje' }, { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' }, { id: 'year', label: 'Ano' },
];

export default function Dashboard() {
  const [period, setPeriod] = useState('today');
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/reports/dashboard?period=${period}`).then((r) => setData(r.data)); }, [period]);

  if (!data) return <div className="p-6 text-center text-slate-500">Carregando...</div>;

  const kpis = [
    { icon: DollarSign, label: 'Receita', v: brl(data.revenue), color: 'text-orange-600', bg: 'bg-orange-50', tid: 'kpi-revenue' },
    { icon: TrendingUp, label: 'Lucro bruto', v: brl(data.profit), color: 'text-green-600', bg: 'bg-green-50', tid: 'kpi-profit' },
    { icon: ShoppingBag, label: 'Vendas', v: data.num_sales, color: 'text-blue-600', bg: 'bg-blue-50', tid: 'kpi-sales' },
    { icon: Package, label: 'Itens vendidos', v: data.items_sold, color: 'text-slate-800', bg: 'bg-slate-100', tid: 'kpi-items' },
    { icon: Wallet, label: 'Despesas', v: brl(data.expenses), color: 'text-red-600', bg: 'bg-red-50', tid: 'kpi-expenses' },
    { icon: Activity, label: 'Resultado líq.', v: brl(data.net_result), color: data.net_result >= 0 ? 'text-green-600' : 'text-red-600', bg: 'bg-slate-100', tid: 'kpi-net' },
    { icon: AlertTriangle, label: 'Avarias', v: brl(data.losses), color: 'text-amber-600', bg: 'bg-amber-50', tid: 'kpi-losses' },
    { icon: Boxes, label: 'Estoque baixo', v: `${data.low_stock_count}+${data.zero_stock_count}`, color: 'text-slate-800', bg: 'bg-slate-100', tid: 'kpi-stock-alert' },
  ];

  return (
    <div className="p-4 h-full overflow-y-auto space-y-4">
      <div className="flex gap-2 overflow-x-auto">
        {PERIODS.map((p) => (
          <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}
            className={`h-9 px-4 rounded-full text-sm font-bold whitespace-nowrap ${period === p.id ? 'bg-orange-500 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k) => (
          <div key={k.label} data-testid={k.tid} className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
            <div className={`w-9 h-9 rounded-xl ${k.bg} grid place-items-center mb-2`}><k.icon size={18} className={k.color}/></div>
            <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide">{k.label}</div>
            <div className={`text-lg font-black ${k.color}`}>{k.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
        <div className="font-bold mb-2">Receita e lucro</div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
            <XAxis dataKey="date" fontSize={10}/>
            <YAxis fontSize={10}/>
            <Tooltip formatter={(v) => brl(v)}/>
            <Line type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={2}/>
            <Line type="monotone" dataKey="profit" stroke="#22C55E" strokeWidth={2}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
        <div className="font-bold mb-2">Vendas por dia</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data.series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
            <XAxis dataKey="date" fontSize={10}/>
            <YAxis fontSize={10}/>
            <Tooltip/>
            <Bar dataKey="sales" fill="#3B82F6" radius={[8, 8, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {(data.zero_stock.length > 0 || data.low_stock.length > 0) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <div className="font-bold mb-2 flex items-center gap-2 text-amber-700"><AlertTriangle size={16}/> Atenção estoque</div>
          <div className="space-y-1 text-sm">
            {data.zero_stock.map((p) => <div key={p.id} className="flex justify-between"><span>{p.name}</span><span className="text-red-600 font-bold">Zerado</span></div>)}
            {data.low_stock.map((p) => <div key={p.id} className="flex justify-between"><span>{p.name}</span><span className="text-amber-600 font-bold">{p.stock} un</span></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
